import { html, nothing, ReactiveController, ReactiveControllerHost, TemplateResult } from 'lit';
import './vto-curated-teaser.js';
import { VtoService } from '../../../scripts/service/vto-service.js';
import { WishlistAppConfig } from '../../../scripts/context/appConfigContext.js';
import { Messages } from '../../../@types/wishlist.js';
import type { PrefetchItem, ProfileImage, VtoStatus } from '../../../scripts/service/vto-types.js';
import { pushToSnackbar } from '../../../scripts/service/snackbar-service.js';
import { saveCuratedListViewMode } from '../../../scripts/utils/view-mode-storage.js';
import { featureSeen } from '../../../scripts/service/feature-seen-service.js';

const VTO_AD_DISMISSED_FEATURE_KEY = 'vto-curated-ad-dismissed';

export interface VtoTeaserConfig {
  isEnabled: () => boolean;
  getAppConfig: () => WishlistAppConfig;
  getVtoService: () => VtoService | undefined;
  getMessages: () => Messages;
  isLoggedIn: () => boolean;
  collectItems: () => PrefetchItem[];
  // Background status pushes (e.g. a failed prefetch) show a snackbar only for hosts that opt in;
  // a host still shows its own upload-click-triggered snackbars (cooldown/generic error) regardless.
  showStatusSnackbars: boolean;
  onPhotoUploaded?: () => void;
}

/**
 * Drives the "upload/change VTO photo" teaser (ad banner or photo-with-change-button) shown at
 * curated-list placements. One controller instance per host; hosts render its top/bottom teasers
 * wherever they apply, so the state, upload flow, and snackbar handling live in exactly one place.
 */
export class VtoTeaserController implements ReactiveController {
  private hasProfileImage = false;
  private vtoChecked = false;
  private vtoProfile: ProfileImage | null = null;
  private adDismissed = false;
  private vtoStatus: VtoStatus = { kind: 'ok' };
  private _profileVersion = 0;

  // Bumped on every successful photo change, so hosts can pass it to already-rendered VTO images
  // (e.g. product tiles) as a cue to discard their stale cached image and refetch.
  get profileVersion(): number {
    return this._profileVersion;
  }

  private readonly host: ReactiveControllerHost;
  private readonly config: VtoTeaserConfig;

  private readonly statusListener = (e: CustomEvent<VtoStatus>) => {
    this.vtoStatus = e.detail;
    if (this.config.showStatusSnackbars) this.showStatusSnackbar(e.detail);
    this.host.requestUpdate();
  };

  constructor(host: ReactiveControllerHost, config: VtoTeaserConfig) {
    this.host = host;
    this.config = config;
    host.addController(this);
  }

  hostConnected(): void {
    this.adDismissed =
      !!this.config.getAppConfig()?.vtoCuratedAdDismissed || featureSeen.isSeen(VTO_AD_DISMISSED_FEATURE_KEY);
    document.addEventListener('ents:vto:status', this.statusListener);
    void this.checkProfile();
  }

  hostDisconnected(): void {
    document.removeEventListener('ents:vto:status', this.statusListener);
  }

  private get visible(): boolean {
    const hidden = this.vtoStatus.kind === 'vto-disabled' || this.vtoStatus.kind === 'session-suspended';
    return !!(
      this.config.isEnabled() &&
      this.vtoChecked &&
      this.config.getAppConfig()?.vtoEnabled &&
      this.config.getVtoService()?.isAvailable() &&
      !hidden
    );
  }

  renderTop(): TemplateResult | typeof nothing {
    if (!this.visible || this.hasProfileImage || this.adDismissed) return nothing;
    return html`<ents-vto-curated-teaser
      variant="ad"
      dismissible
      @ents:vto-teaser:cta="${this.uploadPhoto}"
      @ents:vto-teaser:dismiss="${this.dismissAd}"
      data-test-ents-vto-curated-teaser-top
    ></ents-vto-curated-teaser>`;
  }

  renderBottom(): TemplateResult | typeof nothing {
    if (!this.visible) return nothing;
    if (this.hasProfileImage) {
      return html`<ents-vto-curated-teaser
        variant="photo"
        photo-url="${this.vtoProfile?.url ?? ''}"
        @ents:vto-teaser:cta="${this.uploadPhoto}"
        data-test-ents-vto-curated-teaser-bottom
      ></ents-vto-curated-teaser>`;
    }
    if (this.adDismissed) {
      return html`<ents-vto-curated-teaser
        variant="ad"
        @ents:vto-teaser:cta="${this.uploadPhoto}"
        data-test-ents-vto-curated-teaser-bottom
      ></ents-vto-curated-teaser>`;
    }
    return nothing;
  }

  private async checkProfile(): Promise<void> {
    try {
      const vtoService = this.config.getVtoService();
      if (this.config.isEnabled() && vtoService?.isAvailable() && this.config.getAppConfig()?.vtoEnabled) {
        const profile = await vtoService.getProfileImage();
        this.vtoProfile = profile;
        this.hasProfileImage = !!profile;
      }
    } catch (e) {
      console.error('[vto]', 'checkProfile failed', e);
    } finally {
      this.vtoChecked = true;
      this.host.requestUpdate();
    }
  }

  private dismissAd = (): void => {
    this.adDismissed = true;
    featureSeen.markSeen(VTO_AD_DISMISSED_FEATURE_KEY, {
      loggedIn: this.config.isLoggedIn(),
      endpoint: this.config.getAppConfig().featureApiEndpoint,
    });
    this.host.requestUpdate();
  };

  private uploadPhoto = async (): Promise<void> => {
    const vtoService = this.config.getVtoService();
    if (!vtoService) return;
    if (this.vtoStatus.kind === 'cooldown') {
      void pushToSnackbar(this.config.getMessages()['ents_wishlist_vto_snackbar_cooldown']!);
      return;
    }
    try {
      // requestProfileImage may resolve null even when a profile exists; getProfileImage is authoritative.
      const profile = (await vtoService.requestProfileImage()) ?? (await vtoService.getProfileImage());
      if (!profile) return;
      this.vtoProfile = profile;
      this.hasProfileImage = true;
      this._profileVersion++;
      saveCuratedListViewMode('vto');
      this.config.onPhotoUploaded?.();
      const items = this.config.collectItems();
      if (items.length > 0) await vtoService.prefetchVtos(items);
    } catch (e) {
      console.error('[vto]', 'uploadPhoto failed', e);
      void pushToSnackbar(this.config.getMessages()['ents_wishlist_vto_snackbar_generic_error']!);
    } finally {
      this.host.requestUpdate();
    }
  };

  private showStatusSnackbar(status: VtoStatus): void {
    const messages = this.config.getMessages();
    switch (status.kind) {
      case 'ok':
        return;
      case 'cooldown':
        void pushToSnackbar(messages['ents_wishlist_vto_snackbar_cooldown']!);
        return;
      case 'profile-invalid':
        void pushToSnackbar(
          messages['ents_wishlist_vto_snackbar_profile_invalid']!,
          undefined,
          messages['ents_wishlist_vto_snackbar_profile_invalid_action'],
          () => void this.uploadPhoto(),
        );
        return;
      case 'vto-disabled':
        void pushToSnackbar(messages['ents_wishlist_vto_snackbar_disabled']!);
        return;
      case 'session-suspended':
        void pushToSnackbar(messages['ents_wishlist_vto_snackbar_session_suspended']!);
        return;
      default: {
        console.error('[vto]', 'unhandled VTO status', (status as VtoStatus).kind);
        return;
      }
    }
  }
}
